'use client';

import * as React from 'react';
import { Pipette } from 'lucide-react';
import { cn } from './cn';
import { ColorSwatch } from './ColorSwatch';
import { Popover } from './Popover';
import { useControllableState, useId } from './hooks';

/** Album theme palette + warm neutrals. Matches `AlbumTheme.palette` defaults. */
export const DEFAULT_SWATCHES = [
  '#ffffff', '#faf7f2', '#eae3d8', '#d3c9b9', '#8a7e6c', '#4a463f', '#1b1b1f', '#000000',
  '#c8553d', '#e3957f', '#e0a030', '#f4d48c', '#2e6b52', '#8fb8a4', '#2f5d8a', '#7fa9d6',
];

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function normalizeHex(input: string): string | null {
  const m = HEX_RE.exec(input.trim());
  if (!m) return null;
  const body = m[1] ?? '';
  const full =
    body.length === 3
      ? body
          .split('')
          .map((c) => c + c)
          .join('')
      : body;
  return `#${full.toLowerCase()}`;
}

export interface ColorPickerPanelProps {
  value: string | null;
  onValueChange: (value: string | null) => void;
  /** Swatch grid. Defaults to `DEFAULT_SWATCHES`. */
  swatches?: string[];
  /** Offers a "no colour" (transparent) cell. */
  allowNone?: boolean;
  /** Recently used colours from the editor store, shown in their own row. */
  recent?: string[];
  className?: string;
  labels?: Partial<Record<'palette' | 'recent' | 'custom' | 'none' | 'hex' | 'eyedropper', string>>;
}

/**
 * The picker body: a swatch grid, a hex field and the OS colour dialog.
 * Deliberately not a hand-rolled HSV canvas — the native `<input type="color">`
 * gives the platform picker (and the eyedropper) for free on every device.
 */
export function ColorPickerPanel({
  value,
  onValueChange,
  swatches = DEFAULT_SWATCHES,
  allowNone,
  recent,
  className,
  labels,
}: ColorPickerPanelProps) {
  const t = {
    palette: 'לוח צבעים',
    recent: 'לאחרונה',
    custom: 'צבע מותאם',
    none: 'ללא צבע',
    hex: 'קוד צבע',
    eyedropper: 'בחירת צבע מהמסך',
    ...labels,
  };
  const id = useId('color');
  const [draft, setDraft] = React.useState(value ?? '');
  React.useEffect(() => setDraft(value ?? ''), [value]);

  const commitHex = (raw: string) => {
    const hex = normalizeHex(raw);
    if (hex) onValueChange(hex);
    else setDraft(value ?? '');
  };

  return (
    <div className={cn('flex w-64 flex-col gap-3', className)}>
      <div role="radiogroup" aria-label={t.palette} className="flex flex-col gap-2">
        <p className="text-2xs font-semibold tracking-wide text-ink-3 uppercase">{t.palette}</p>
        <div className="grid grid-cols-8 gap-1.5">
          {allowNone ? (
            <ColorSwatch
              color={null}
              size="sm"
              label={t.none}
              selected={value === null}
              onClick={() => onValueChange(null)}
            />
          ) : null}
          {swatches.map((c) => (
            <ColorSwatch
              key={c}
              color={c}
              size="sm"
              selected={value?.toLowerCase() === c.toLowerCase()}
              onClick={() => onValueChange(c)}
            />
          ))}
        </div>
      </div>

      {recent?.length ? (
        <div role="radiogroup" aria-label={t.recent} className="flex flex-col gap-2">
          <p className="text-2xs font-semibold tracking-wide text-ink-3 uppercase">{t.recent}</p>
          <div className="flex flex-wrap gap-1.5">
            {recent.map((c) => (
              <ColorSwatch
                key={c}
                color={c}
                size="sm"
                selected={value?.toLowerCase() === c.toLowerCase()}
                onClick={() => onValueChange(c)}
              />
            ))}
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <label htmlFor={id} className="text-2xs font-semibold tracking-wide text-ink-3 uppercase">
          {t.custom}
        </label>
        <div className="flex items-center gap-2">
          {/* Native colour dialog — free platform UI, incl. the eyedropper. */}
          <span className="relative">
            <input
              type="color"
              aria-label={t.eyedropper}
              value={value ?? '#ffffff'}
              onChange={(e) => onValueChange(e.target.value)}
              className="size-touch cursor-pointer rounded-md border border-line-strong bg-surface-2 p-1"
            />
            <Pipette className="pointer-events-none absolute -bottom-0.5 -end-0.5 size-3.5 rounded-full bg-surface p-px text-ink-3" />
          </span>
          <input
            id={id}
            dir="ltr"
            inputMode="text"
            spellCheck={false}
            autoComplete="off"
            value={draft}
            placeholder="#c8553d"
            onChange={(e) => setDraft(e.target.value)}
            onBlur={(e) => commitHex(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitHex(e.currentTarget.value);
              }
            }}
            className={cn(
              'h-touch min-w-0 flex-1 rounded-md border border-line bg-surface-2 px-3',
              'font-mono text-sm text-ink uppercase tabular-nums',
              'focus-visible:border-accent focus-visible:outline-none',
              'focus-visible:shadow-[0_0_0_3px_var(--color-accent-soft-2)]',
            )}
          />
        </div>
      </div>
    </div>
  );
}

export interface ColorPickerProps extends Omit<ColorPickerPanelProps, 'value' | 'onValueChange'> {
  value?: string | null;
  defaultValue?: string | null;
  onValueChange?: (value: string | null) => void;
  /** Text beside the trigger swatch. */
  label?: React.ReactNode;
  /** Accessible name for the trigger when there is no visible label. */
  triggerLabel?: string;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

/** Trigger swatch + popover panel. Use `ColorPickerPanel` directly inside a Sheet. */
export function ColorPicker({
  value,
  defaultValue = '#c8553d',
  onValueChange,
  label,
  triggerLabel = 'בחירת צבע',
  disabled,
  size = 'md',
  ...panel
}: ColorPickerProps) {
  const [current, setCurrent] = useControllableState<string | null>(value, defaultValue, onValueChange);

  const trigger = (
    <button
      type="button"
      disabled={disabled}
      aria-label={label ? undefined : triggerLabel}
      className={cn(
        'inline-flex min-h-touch items-center gap-2 rounded-md border border-line bg-surface-2 px-2',
        'cursor-pointer text-sm text-ink transition-colors hover:border-line-strong',
        'disabled:pointer-events-none disabled:opacity-45',
      )}
    >
      <ColorSwatch color={current} size={size} readOnly />
      {label ? <span className="truncate">{label}</span> : null}
      <span dir="ltr" className="font-mono text-xs text-ink-3 tabular-nums">
        {current ?? '—'}
      </span>
    </button>
  );

  return (
    <Popover trigger={trigger} align="start">
      <ColorPickerPanel value={current} onValueChange={setCurrent} {...panel} />
    </Popover>
  );
}
